/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query';
import {
    DateFormats,
    formatDate,
    formatGender,
    formatNameLastFirst,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabel,
    DetailLabelList,
    DetailLabelValuePair,
    PatientInfoList,
} from '@strata/react-label-value-pairs';
import { InformationIcon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { isGrandfatheredReferral } from 'constants/grandfatheredReferral';
import {
    computeWillBeUnmanagedOnDate,
    getMemberEligibilityStatusDisplay,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import { useGetDiagnosisQuery } from 'store/api/referenceApi';
import {
    useGetBenefitCheckConfigQuery,
    useGetMemberDetailsQuery,
} from 'store/api/scopeAssessorApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface PatientDetailsProps {
    showFullDetails: boolean;
    selectedReferral?: {
        payerId: string;
        referralId: string;
        valueCases: { strat_rule_name: string; value_case_name?: string }[];
        incentiveTotal?: number;
    };
    referralDetails?: ReferralDetails;
}

export const PatientDetails = ({
    showFullDetails,
    selectedReferral = undefined,
    referralDetails = undefined,
}: PatientDetailsProps) => {
    const { marketplaceEnhancedEligibility, showGrandfatheredPharmacy } = useFlags<{
        marketplaceEnhancedEligibility: boolean;
        showGrandfatheredPharmacy: boolean;
    }>();
    const attributeValues = [
        ...(selectedReferral?.valueCases ?? []),
        ...(isGrandfatheredReferral(referralDetails, showGrandfatheredPharmacy)
            ? [{ strat_rule_name: PatientInfoList.Types.GRANDFATHERED }]
            : []),
    ];
    const memberDetailsQueryArgs =
        referralDetails?.memberId &&
        referralDetails.payerId &&
        referralDetails.memberFirstName &&
        referralDetails.memberLastName
            ? {
                  segmentId: referralDetails.segmentId,
                  memberId: referralDetails.memberId,
                  firstName: referralDetails.memberFirstName,
                  lastName: referralDetails.memberLastName,
                  dateOfBirth: referralDetails.memberDOB,
                  payerId: referralDetails.payerId,
              }
            : skipToken;
    const { data: memberDetails, isFetching: isMemberDetailsFetching } =
        useGetMemberDetailsQuery(memberDetailsQueryArgs);
    const isPendingEnrollment = memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;
    const shouldFetchBenefitCheckConfig =
        marketplaceEnhancedEligibility &&
        memberDetailsQueryArgs !== skipToken &&
        isPendingEnrollment;
    const { data: delayedUnmanagementDays } = useGetBenefitCheckConfigQuery(
        shouldFetchBenefitCheckConfig
            ? {
                  payerId: memberDetailsQueryArgs.payerId,
                  segmentId: memberDetailsQueryArgs.segmentId,
              }
            : skipToken,
    );
    const { data: diagnosisData, isFetching: isDiagnosisFetching } = useGetDiagnosisQuery(
        referralDetails?.diagnosisOid ? { diagnosisOid: referralDetails.diagnosisOid } : skipToken,
    );
    const eligibilityDisplay = memberDetails
        ? getMemberEligibilityStatusDisplay(memberDetails.enrollmentStatus)
        : null;
    const enrollmentStatusDisplay =
        showFullDetails && marketplaceEnhancedEligibility ? eligibilityDisplay : null;
    const gracePeriodEnds =
        computeWillBeUnmanagedOnDate(memberDetails?.pendingAt, delayedUnmanagementDays) ??
        memberDetails?.willBeUnmanagedOn;
    const showGracePeriodEnds = isPendingEnrollment && !!gracePeriodEnds;

    return (
        <FormSectionWithRows
            sectionTitle="Patient Details"
            isLoading={!referralDetails || isMemberDetailsFetching || isDiagnosisFetching}
            rowOneContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Name"
                            value={formatNameLastFirst(
                                memberDetails?.lastName,
                                memberDetails?.firstName,
                            )}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Gender"
                            value={formatGender(memberDetails?.gender)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="DOB"
                            value={formatDate(memberDetails?.dateOfBirth)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Age"
                            value={getAge(memberDetails?.dateOfBirth)}
                        />
                    </>
                ) : (
                    <>
                        <AddressDetailLabelValue
                            style={{ width: '25%' }}
                            label="Location"
                            city={referralDetails?.memberCity}
                            state={referralDetails?.memberState}
                            zip={referralDetails?.memberZip}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Age"
                            value={getAge(referralDetails?.memberDOB)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Diagnosis"
                            value={diagnosisData?.description}
                        />
                        <PatientInfoList
                            style={{ width: '25%' }}
                            label="Attributes"
                            incentiveTotal={selectedReferral?.incentiveTotal}
                            values={attributeValues}
                        />
                    </>
                )
            }
            rowTwoContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Diagnosis"
                            value={diagnosisData?.description}
                        />
                        <AddressDetailLabelValue
                            style={{ width: '25%' }}
                            address1={memberDetails?.memberAddress1}
                            address2={memberDetails?.memberAddress2}
                            city={memberDetails?.memberCity}
                            state={memberDetails?.memberState}
                            zip={memberDetails?.memberZip}
                        />
                        <DetailLabelList
                            label="Phone"
                            style={{ width: '25%' }}
                            values={[
                                formatPhoneNumber(memberDetails?.phone),
                                ...(memberDetails?.secondaryPhone
                                    ? [
                                          // eslint-disable-next-line react/jsx-key
                                          <div
                                              className="flex__gap--x-small"
                                              style={{ alignItems: 'center' }}
                                          >
                                              <span>
                                                  {formatPhoneNumber(memberDetails.secondaryPhone)}
                                              </span>
                                              <InformationIcon tooltipText="Payer-submitted supplementary phone number" />
                                          </div>,
                                      ]
                                    : []),
                            ]}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Email"
                            value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                        />
                    </>
                ) : undefined
            }
            rowThreeContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Member ID"
                            value={memberDetails?.memberId}
                        />
                        <PatientInfoList
                            style={{ width: '25%' }}
                            label="Attributes"
                            incentiveTotal={selectedReferral?.incentiveTotal}
                            values={attributeValues}
                        />
                    </>
                ) : undefined
            }
            rowFourContent={
                enrollmentStatusDisplay ? (
                    <>
                        <div style={{ width: '25%' }}>
                            <DetailLabel label="Enrollment Status" />
                            <StatusIndicator
                                message={enrollmentStatusDisplay.label}
                                status={enrollmentStatusDisplay.status}
                                tooltipText={enrollmentStatusDisplay.tooltipText}
                            />
                        </div>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Enrollment change date"
                            value={formatDate(memberDetails?.lastUpdated, DateFormats.DATE_TIME)}
                        />
                        {showGracePeriodEnds ? (
                            <DetailLabelValuePair
                                style={{ width: '25%' }}
                                label="Grace period ends"
                                value={formatDate(gracePeriodEnds)}
                            />
                        ) : null}
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Coverage dates"
                            value={
                                <EnrollmentCoverageDates
                                    enrollmentStart={memberDetails?.enrollmentStart}
                                    enrollmentEnd={memberDetails?.enrollmentEnd}
                                    isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                                />
                            }
                        />
                    </>
                ) : undefined
            }
        />
    );
};
